import { extractJsonObject } from "../outline/outlinePrompt.js";
import { defaultStyleAspects } from "./presets.js";
import { mergeSurfStyle } from "./surfStyle.js";
export const SURF_STYLE_EXPAND_SYSTEM_ILLUSTRATIVE = [
    "You are a senior art director. You receive a short style keyword from the user and produce structured JSON only.",
    "Never include markdown code fences. Never include commentary outside the JSON object.",
    "derivedPrompt MUST describe TREATMENT ONLY: medium, lighting source and direction, color mood, line quality, texture, composition habits.",
    "It MUST NOT name a concrete subject, character, location, or scene scenario — subject matter goes only in subjectMatter.",
    "You deliberately introduce variety: the same STYLE_KEYWORD with a different VARIATION_SEED must yield a meaningfully different treatment angle.",
].join("\n");
export const SURF_STYLE_EXPAND_SYSTEM_PHOTOREAL = [
    "You are a senior photographic director. You receive a short style keyword describing a real-world photographic genre and produce structured JSON only.",
    "Never include markdown code fences. Never include commentary outside the JSON object.",
    "derivedPrompt MUST describe TREATMENT ONLY and target photorealistic AI image generation.",
    "It MUST NOT name a concrete subject, character, location, or scene scenario — subject matter goes only in subjectMatter.",
    "derivedPrompt MUST target photorealistic AI image generation only:",
    "- name a real camera + lens + film stock or sensor profile when credible;",
    "- specify lighting source, depth of field, focal length, shutter feel;",
    "- call out true-to-life skin pores / material micro-texture when relevant;",
    "- explicitly forbid illustration / painting / CGI / 3D render / cartoon / cel shading / vector graphics.",
    "Never write words like illustration, vector, flat shapes, paper craft, brush stroke, watercolor, pixel art.",
].join("\n");
const USER_INSTRUCTIONS_ILLUSTRATIVE = `Respond with ONLY a single JSON object (no markdown fences, no extra text) using this exact schema:
{"derivedPrompt":"<string>","paletteBackground":["#RRGGBB","#RRGGBB",...],"paletteText":["#RRGGBB","#RRGGBB",...],"subjectMatter":"<short EN, max 120 chars>"}

Rules:
- derivedPrompt: English art-direction for image generation — TREATMENT ONLY. Max 1200 characters. Do NOT name subjects, characters, locations, or concrete scene narratives.
- subjectMatter: one short English phrase for diversity tracking only. Max 120 characters. NOT copied into slide prompts.
- When AVOID_RECENT_SCENARIOS is listed, do not repeat those scenarios or near-synonyms in subjectMatter.
- paletteBackground: 3 to 6 dominant hex colors starting with #.
- paletteText: 3 to 6 hex colors for typographic accents over paletteBackground.`;
const USER_INSTRUCTIONS_PHOTOREAL = `Respond with ONLY a single JSON object (no markdown fences, no extra text) using this exact schema:
{"derivedPrompt":"<string>","paletteBackground":["#RRGGBB","#RRGGBB",...],"paletteText":["#RRGGBB","#RRGGBB",...],"subjectMatter":"<short EN, max 120 chars>"}

Rules:
- derivedPrompt: English photographic direction — TREATMENT ONLY. Max 1200 characters. End with explicit photoreal negatives.
- subjectMatter: one short English photographic scenario for diversity tracking only. Max 120 characters.
- When AVOID_RECENT_SCENARIOS is listed, do not repeat those scenarios in subjectMatter.
- paletteBackground: 3 to 6 realistic environment hex colors.
- paletteText: 3 to 6 hex colors for typographic accents.
- NEVER use illustration, vector, flat, painterly, or CGI vocabulary in derivedPrompt.`;
export const SURF_STYLE_ANALYZE_SYSTEM = [
    "Analyze the reference image for presentation slide BACKGROUND style only.",
    "Extract look-only art direction: palette, lighting, medium, mood, realism.",
    "Do NOT describe subjects, faces, or readable text in the image.",
    "Respond ONLY with JSON:",
    '{"derivedPrompt":"...","backgroundRealism":"photoreal"|"illustrative","paletteBackground":["#hex",...],"paletteText":["#hex",...]}',
].join("\n");
export function surfStyleExpandSystem(realism) {
    return realism === "photoreal" ? SURF_STYLE_EXPAND_SYSTEM_PHOTOREAL : SURF_STYLE_EXPAND_SYSTEM_ILLUSTRATIVE;
}
export function surfStyleExpandUserPrompt(opts) {
    const lines = [
        `STYLE_KEYWORD: ${opts.prePrompt.trim()}`,
        opts.variationSeed ? `VARIATION_SEED: ${opts.variationSeed}` : "",
        `REALISM_MODE: ${opts.realism}`,
        opts.intentHint?.trim() ? `INTENT_HINT: ${opts.intentHint.trim()}` : "",
        opts.avoidRecentScenarios?.length
            ? `AVOID_RECENT_SCENARIOS: ${opts.avoidRecentScenarios.join(" | ")}`
            : "",
        "",
        opts.realism === "photoreal" ? USER_INSTRUCTIONS_PHOTOREAL : USER_INSTRUCTIONS_ILLUSTRATIVE,
    ];
    return lines.filter((l) => l !== "").join("\n");
}
function normalizeHexList(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw
        .map((x) => String(x).trim())
        .filter((h) => /^#[0-9a-fA-F]{6}$/.test(h))
        .slice(0, 6);
}
function palettesToSurfPalette(bg, tx) {
    return {
        surface: bg[0] ?? "#f8fafc",
        text: tx[0] ?? "#0f172a",
        accent: tx[1] ?? bg[1] ?? "#38bdf8",
        muted: bg[2] ?? tx[2] ?? "#94a3b8",
    };
}
export function parseStyleExpandOutput(raw) {
    const obj = extractJsonObject(raw);
    if (!obj)
        throw new Error("Risposta espansione stile non JSON.");
    const derivedPrompt = String(obj.derivedPrompt ?? "").trim().slice(0, 1200);
    if (!derivedPrompt)
        throw new Error("derivedPrompt mancante.");
    assertLookOnlyBackgroundTreatment(derivedPrompt);
    const paletteBackground = normalizeHexList(obj.paletteBackground);
    const paletteText = normalizeHexList(obj.paletteText);
    if (!paletteBackground.length || !paletteText.length) {
        throw new Error("paletteBackground/paletteText mancanti o non validi.");
    }
    const subjectMatter = String(obj.subjectMatter ?? "").trim().slice(0, 120) || undefined;
    return { derivedPrompt, paletteBackground, paletteText, subjectMatter };
}
export function applyStyleExpandOutput(base, output) {
    return mergeSurfStyle(base, {
        derivedPrompt: output.derivedPrompt,
        backgroundTreatment: output.derivedPrompt,
        subjectMatter: output.subjectMatter,
        paletteBackground: output.paletteBackground,
        paletteText: output.paletteText,
        palette: palettesToSurfPalette(output.paletteBackground, output.paletteText),
        styleAspects: base.styleAspects ?? defaultStyleAspects(),
    });
}
export function parseStyleExpandResponse(raw, base) {
    return applyStyleExpandOutput(base, parseStyleExpandOutput(raw));
}
export function parseStyleAnalyzeResponse(raw, base) {
    const obj = extractJsonObject(raw);
    if (!obj)
        throw new Error("Risposta analisi stile non JSON.");
    const derivedPrompt = String(obj.derivedPrompt ?? obj.backgroundTreatment ?? "").trim();
    if (!derivedPrompt)
        throw new Error("derivedPrompt mancante.");
    assertLookOnlyBackgroundTreatment(derivedPrompt);
    const realismRaw = String(obj.backgroundRealism ?? base.backgroundRealism).toLowerCase();
    const backgroundRealism = realismRaw === "photoreal" || realismRaw === "illustrative" ? realismRaw : base.backgroundRealism;
    const paletteBackground = normalizeHexList(obj.paletteBackground);
    const paletteText = normalizeHexList(obj.paletteText);
    const palette = paletteBackground.length && paletteText.length
        ? palettesToSurfPalette(paletteBackground, paletteText)
        : base.palette;
    return mergeSurfStyle(base, {
        derivedPrompt,
        backgroundTreatment: derivedPrompt,
        backgroundRealism,
        paletteBackground: paletteBackground.length ? paletteBackground : base.paletteBackground,
        paletteText: paletteText.length ? paletteText : base.paletteText,
        palette,
    });
}
export function stylePalettePromptSegments(style) {
    const aspects = style.styleAspects ?? defaultStyleAspects();
    if (!aspects.injectPaletteInPrompt)
        return [];
    const segments = [];
    if (aspects.injectBackgroundPalette !== false && style.paletteBackground?.length) {
        segments.push(`Background palette: ${style.paletteBackground.join(", ")}`);
    }
    if (aspects.injectTextPalette !== false && style.paletteText?.length) {
        segments.push(`Text/accent palette: ${style.paletteText.join(", ")}`);
    }
    if (!style.paletteBackground?.length && aspects.injectPaletteInPrompt) {
        segments.push(`Palette mood: surface ${style.palette.surface}, text ${style.palette.text}, accent ${style.palette.accent}`);
    }
    return segments;
}
export function styleTreatmentForPrompt(style) {
    const aspects = style.styleAspects ?? defaultStyleAspects();
    if (aspects.injectDerivedPromptInImagePrompt === false) {
        return style.backgroundTreatment;
    }
    return style.derivedPrompt?.trim() || style.backgroundTreatment;
}
/** Contextual draft preview — subject-focused artwork, no baked typography (hybrid slide model). */
export function buildStylePreviewDraftPrompt(style, subject) {
    const treatment = styleTreatmentForPrompt(style);
    const subjectLine = subject?.trim()
        ? `Main subject (focus of the illustration, authoritative): ${subject.trim()}`
        : "Subject: abstract concept representing the deck topic.";
    return [
        "16:9 presentation slide artwork draft preview.",
        subjectLine,
        `Visual style / treatment: ${treatment}`,
        ...stylePalettePromptSegments(style),
        `Realism: ${style.backgroundRealism}`,
        "CRITICAL: NO readable text, letters, numbers, captions, labels, or UI in the image — sample text is overlaid as editable widgets.",
        "Leave calm uncluttered areas in the safe zones for overlay text.",
    ]
        .filter(Boolean)
        .join(" ");
}
/** @deprecated Use buildStylePreviewDraftPrompt */
export function buildStylePreviewImagePrompt(style) {
    return buildStylePreviewDraftPrompt(style);
}
const FORBIDDEN_IN_TREATMENT = /\b(slide title|headline|subtitle|bullet list|readable text|typography in frame|logo in frame)\b/i;
export function assertLookOnlyBackgroundTreatment(treatment) {
    if (FORBIDDEN_IN_TREATMENT.test(treatment)) {
        throw new Error("derivedPrompt deve essere look-only (no testo/soggetti nel frame).");
    }
}
